const DateFormatter = Object.create(null);
const datefns = require('date-fns');
const momentLib = require('moment');
const moment = momentLib.default || momentLib;

DateFormatter.formatDate = formatDate;
DateFormatter.formatHour = formatHour;
DateFormatter.getDatepickerFormat = getDatepickerFormat;
DateFormatter.getDatepickerHourFormat = getDatepickerHourFormat;
DateFormatter.fromDateToDate = fromDateToDate;
DateFormatter.getAge = getAge;
DateFormatter.formatDuration = formatDuration;
DateFormatter.getTimeRanges = getTimeRanges;
DateFormatter.getTimesBetweenTwoDates = getTimesBetweenTwoDates;
DateFormatter.normalizeDate = normalizeDate;
DateFormatter.getDateStartEndStr = getDateStartEndStr;
DateFormatter.getEventDurationStr = getEventDurationStr;
DateFormatter.getEventDurationObj = getEventDurationObj;

moment.locale('fr');

/**
 * @typedef {'minute'|'hour'|'day'|'month'} DurationType - Unité d'une durée
 * @typedef {object} DurationObj - Durée entre deux dates
 * @property {number} number - Nombre d'unités
 * @property {DurationType} type - Unité de la durée
 */

/** @type {Record<DurationType, string>} */
const DURATION_TYPE_LABELS = {
  minute: 'minute',
  hour: 'heure',
  day: 'jour',
  month: 'mois'
};

/**
 * Permet de formatter une date
 * @param {Date|string} datetime - Date à formatter
 * @param {boolean} [hours] - True pour afficher les heures sinon false pour les cacher
 * @param {boolean} [onlyHours] - True pour n'afficher que l'heure sinon false pour afficher la date complète
 * @param {boolean} [withoutYear] - True pour afficher que les heures sinon false pour afficher la date complète
 * @returns {string}
 */
// eslint-disable-next-line complexity
function formatDate(datetime, hours, onlyHours, withoutYear) {
  if (datetime && datetime.toString() !== 'Invalid Date' && !isNaN(new Date(datetime))) {
    datetime = new Date(datetime);
    if (datetime) {
      var day = datetime.getDate() <= 9 ? `0${ datetime.getDate() }` : datetime.getDate(),
          month = datetime.getMonth() + 1 <= 9 ? `0${ datetime.getMonth() + 1 }` : datetime.getMonth() + 1,
          year = datetime.getFullYear(),
          hour = datetime.getHours() < 10 ? `0${ datetime.getHours() }` : datetime.getHours(),
          minute = datetime.getMinutes() < 10 ? `0${ datetime.getMinutes() }` : datetime.getMinutes();

      if (hours) {
        // eslint-disable-next-line max-depth
        if (withoutYear) {
          return onlyHours ? `${ hour }h${ minute }` : `${ day }/${ month } à ${ hour }h${ minute }`;
        } else {
          return onlyHours ? `${ hour }h${ minute }` : `${ day }/${ month }/${ year } à ${ hour }h${ minute }`;
        }
      } else {
        // eslint-disable-next-line max-depth
        if (withoutYear) {
          return `${ day }/${ month }`;
        } else {
          return `${ day }/${ month }/${ year }`;
        }
      }
    } else {
      return null;
    }
  } else {
    return 'Aucune date';
  }
}

/**
 * Permet de remplacer le : par un h dans l'heure au format string
 * @param {string} hour - Heure à formatter
 * @returns {string}
 */
function formatHour(hour) {
  if (!hour) {
    return '';
  }

  if (!hour.match(/:/g)) {
    return hour;
  }

  return hour.replace(':', 'h');
}

/**
 * Permet de formatter la date pour les champs datepicker
 * @param {Date} datetime - Date à formatter
 * @returns {?string}
 */
function getDatepickerFormat(datetime) {
  if (datetime) {
    datetime = new Date(datetime);
    var day = datetime.getDate() <= 9 ? `0${ datetime.getDate() }` : datetime.getDate(),
        month = datetime.getMonth() + 1 <= 9 ? `0${ datetime.getMonth() + 1 }` : datetime.getMonth() + 1,
        year = datetime.getFullYear();

    return `${ day }/${ month }/${ year }`;
  } else {
    return null;
  }
}

/**
 * Permet de formatter la date en retournant uniquement l'heure au bon format pour les champs datepicker
 * @param {Date} datetime - Date à formatter
 * @returns {?string}
 */
function getDatepickerHourFormat(datetime) {
  var date, hours, minutes;

  date = datetime ? new Date(datetime) : null;

  if (date) {
    hours = date.getHours() <= 9 ? `0${ date.getHours() }` : date.getHours();
    minutes = date.getMinutes() <= 9 ? `0${ date.getMinutes() }` : date.getMinutes();
    return `${ hours }:${ minutes }`;
  } else {
    return null;
  }
}

/**
 * Permet d'afficher une textuellement une période entre une date de début et de fin pour les vues
 * @param {Date} start - Date de début
 * @param {Date} end - Date de fin
 * @param {boolean} withoutYear - True si on ne veut pas afficher les heures sinon false
 * @param {boolean} [hours=false] - True si on veut afficher les heures sinon false
 * @returns {string}
 */
// eslint-disable-next-line complexity
function fromDateToDate(start, end, withoutYear, hours = false) {
  if (start && end) {
    return `Du ${ formatDate(start, hours, false, withoutYear) } au ${ formatDate(end, hours, false, withoutYear) }`;
  } else if (!start && end) {
    return `Jusqu'au ${ formatDate(end, hours, false, withoutYear) }`;
  } else if (start && !end) {
    return `Le ${ formatDate(start, hours, false, withoutYear) }`;
  } else {
    return '';
  }
}

/**
 * Permet de retourner l'âge à partir d'une date de naissance
 * @param {string} dateString - Date de naissance au format string
 * @returns {string}
 */
function getAge(dateString) {
  var today = moment(),
      birthDate;

  if (!dateString) {
    return null;
  }

  birthDate = moment(new Date(dateString));

  if (birthDate.isAfter(today)) {
    return null;
  }

  return today.diff(birthDate, 'years');
}

/**
 * Permet de formatter une durée en minutes, heures ou secondes sous forme de texte
 * @param {number} minuteNumber - Nombre de minutes
 * @param {'minutes'|'hours'|'seconds'} [formatTo] - Format de retour
 * @returns {string} - Chaîne formattée
 */
function formatDuration(minuteNumber, formatTo = 'minutes') {
  var str,
      format = formatTo || 'minutes',
      calculatedNumber = minuteNumber;

  if (!minuteNumber) {
    return null;
  }

  switch (format) {
    case 'seconds':
      calculatedNumber = minuteNumber * 60;
      str = `${ calculatedNumber } seconde(s)`;
      break;
    case 'minutes':
      str = `${ calculatedNumber } minute(s)`;
      break;
    case 'hours':
      calculatedNumber = minuteNumber / 60;
      str = `${ calculatedNumber } heure(s)`;
      break;
  }
  return str;
}

/**
 * Permet de générer un tableau d'heures en fonction d'une interval passé en paramètre
 * @param {number} interval - Heures toutes les X minutes
 * @returns {Array<string>} Le tableau des heures
 */
function getTimeRanges(interval) {
  var ranges = [],
      hour,
      minute,
      i;

  if (interval <= 0) {
    return [];
  }

  for (i = 0; i < 24 * 60; i += interval) {
    hour = Math.floor(i / 60);
    minute = i % 60;
    ranges.push(`${ `0${ hour }`.slice(-2) }:${ `0${ minute }`.slice(-2) }`);
  }


  // NOTE on ajoute 23:59 à la fin des plages horaires pour permettre de sélectionner la fin de journée dans les sélecteurs d'heure si un modèle l'a appliquée à une heure
  ranges.push('23:59');

  return ranges;
}

/**
 * Permet de récupérer un temps entre deux dates
 * @param {string} startDate - Date de début
 * @param {string} endDate - Date de fin
 * @param {'minutes'|'hours'|'seconds'|'days'} [unit='days'] - Unité de temps pour le résultat
 * @return {number} Temps entre les deux dates dans l'unité choisie
 */
function getTimesBetweenTwoDates(startDate, endDate, unit = 'days') {
  const start = new Date(startDate);
  const end = new Date(endDate);

  switch (unit) {
    case 'minutes':
      return datefns.differenceInMinutes(end, start);
    case 'hours':
      return datefns.differenceInHours(end, start);
    case 'seconds':
      return datefns.differenceInSeconds(end, start);
    case 'days':
    default:
      return datefns.differenceInDays(end, start);
  }
}

/**
 * Permet de normaliser une date en mettant les heures, minutes, secondes et millisecondes à 0
 * @param {Date} date - Date à normaliser
 * @returns {Date} Date normalisée
 * @private
 */
function normalizeDate(date) {
  return datefns.set(date, {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0
  });
}

/**
 * Permet d'afficher textuellement une date de début et une date de fin optionnelle pour les vues
 * - Sans date de fin ou sur une seule journée : "Le 12/06 à 14:00"
 * - Sur plusieurs jours : "Du 12/06 au 14/06", avec l'année si les deux dates ne sont pas sur la même année "Du 30/12/2026 au 02/01/2027"
 * @param {Date|string} startDate - Date de début
 * @param {?(Date|string)} endDate - Date de fin
 * @returns {string} Chaîne représentant la période
 * TU DONE 🗸
 */
function getDateStartEndStr(startDate, endDate) {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;

  if (!end || datefns.isSameDay(start, end)) {
    return `Le ${ datefns.format(start, 'dd/MM \'à\' HH:mm') }`;
  }

  const dateFormat = datefns.isSameYear(start, end) ? 'dd/MM' : 'dd/MM/yyyy';

  return `Du ${ datefns.format(start, dateFormat) } au ${ datefns.format(end, dateFormat) }`;
}

/**
 * Permet de construire la chaîne d'affichage de la durée entre deux dates
 * - Le même jour : en heures "3 heures" (en minutes "45 minutes" si moins d'une heure)
 * - Pas le même jour mais le même mois : en jours calendaires "3 jours"
 * - Pas le même mois : en mois "2 mois" (30 jours par mois, 1 mois minimum)
 * @param {Date|string} startDate - Date de début
 * @param {?(Date|string)} endDate - Date de fin
 * @returns {?string} Chaîne représentant la durée entre les deux dates ou null s'il n'y a pas de date de fin
 * TU DONE 🗸
 */
function getEventDurationStr(startDate, endDate) {
  const durationObj = getEventDurationObj(startDate, endDate);

  if (!durationObj) {
    return null;
  }

  const unit = DURATION_TYPE_LABELS[durationObj.type];

  // NOTE "mois" est invariable au pluriel
  return `${ durationObj.number } ${ unit }${ durationObj.number > 1 && durationObj.type !== 'month' ? 's' : '' }`;
}

/**
 * Permet de récupérer l'objet représentant la durée entre deux dates
 * - Le même jour : en heures { number: 3, type: 'hour' } (en minutes { number: 45, type: 'minute' } si moins d'une heure)
 * - Pas le même jour mais le même mois : en jours calendaires { number: 3, type: 'day' }
 * - Pas le même mois : en mois { number: 2, type: 'month' } (30 jours par mois, 1 mois minimum)
 * @param {Date|string} startDate - Date de début
 * @param {?(Date|string)} endDate - Date de fin
 * @returns {?DurationObj} Objet représentant la durée entre les deux dates ou null s'il n'y a pas de date de fin
 * TU DONE 🗸
 */
function getEventDurationObj(startDate, endDate) {
  if (!endDate) {
    return null;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (datefns.isSameDay(start, end)) {
    const nbHours = datefns.differenceInHours(end, start);

    return nbHours ? { number: nbHours, type: 'hour' } : { number: datefns.differenceInMinutes(end, start), type: 'minute' };
  }

  if (datefns.isSameMonth(start, end)) {
    return { number: datefns.differenceInCalendarDays(end, start) + 1, type: 'day' };
  }

  return { number: Math.max(1, Math.round(datefns.differenceInDays(end, start) / 30)), type: 'month' };
}

module.exports = DateFormatter;
